/**
 * Life Balance Wheel — Premium SVG radar/pie chart with 8 life dimensions.
 * Inspired by reference: umr 7.jpg, umr 8.jpg — colorful concentric wheel.
 */

import { memo, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLifeBalanceStore, type SegmentId, type WheelSegment } from '@turnifly/shared/stores'
import { WheelSegmentSheet } from './wheel-segment-sheet'

const SIZE = 280
const CENTER = SIZE / 2
const MAX_RADIUS = 120
const INNER_RADIUS = 20
const RINGS = 5

interface SegmentConfig {
  id: SegmentId
  labelKey: string
  emoji: string
  color: string
  colorLight: string
}

const SEGMENTS: SegmentConfig[] = [
  { id: 'din', labelKey: 'life.balance.din', emoji: '🕌', color: '#EF4444', colorLight: '#EF444440' },
  { id: 'karyera', labelKey: 'life.balance.karyera', emoji: '💼', color: '#F59E0B', colorLight: '#F59E0B40' },
  { id: 'pul', labelKey: 'life.balance.pul', emoji: '💰', color: '#1F2937', colorLight: '#6B728040' },
  { id: 'osish', labelKey: 'life.balance.osish', emoji: '📈', color: '#0061FF', colorLight: '#0061FF40' },
  { id: 'soglik', labelKey: 'life.balance.soglik', emoji: '💪', color: '#22C55E', colorLight: '#22C55E40' },
  { id: 'dustlar', labelKey: 'life.balance.dustlar', emoji: '👥', color: '#F97316', colorLight: '#F9731640' },
  { id: 'oila', labelKey: 'life.balance.oila', emoji: '👨‍👩‍👧', color: '#EC4899', colorLight: '#EC489940' },
  { id: 'xobbi', labelKey: 'life.balance.xobbi', emoji: '🎨', color: '#A855F7', colorLight: '#A855F740' },
]

export const LifeBalance = memo(function LifeBalance() {
  const { t } = useTranslation()
  const { segments: segmentsList, editingSegmentId, setEditing } = useLifeBalanceStore()

  // Build lookup map from array
  const segmentMap = useMemo(() => {
    const map: Record<string, number> = {}
    segmentsList.forEach((s: WheelSegment) => { map[s.id] = s.score })
    return map
  }, [segmentsList])

  const count = SEGMENTS.length
  const angleStep = (2 * Math.PI) / count

  // Point on circle
  const pointAt = (angle: number, radius: number) => ({
    x: CENTER + radius * Math.cos(angle - Math.PI / 2),
    y: CENTER + radius * Math.sin(angle - Math.PI / 2),
  })

  // Build filled polygon from scores
  const polygonPoints = SEGMENTS.map((seg, i) => {
    const score = (segmentMap[seg.id] ?? 5)
    const radius = INNER_RADIUS + ((MAX_RADIUS - INNER_RADIUS) * score) / 10
    const p = pointAt(i * angleStep, radius)
    return `${p.x},${p.y}`
  }).join(' ')

  const handleSegmentTap = useCallback((id: SegmentId) => {
    setEditing(id)
  }, [setEditing])

  // Average score
  const avg = segmentsList.reduce((sum: number, s: WheelSegment) => sum + s.score, 0) / count

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03]">
        <div className="flex items-center justify-between px-4 pt-4 pb-1">
          <div>
            <h3 className="text-sm font-bold text-text">{t('life.lifeBalance')}</h3>
            <p className="text-[11px] text-hint mt-0.5">{t('life.tapToRate')}</p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08]">
            <span className="text-xs font-bold text-text">{avg.toFixed(1)}</span>
            <span className="text-[10px] text-hint">/ 10</span>
          </div>
        </div>

        <div className="flex justify-center py-3">
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="select-none">
            <defs>
              <radialGradient id="wheelBg">
                <stop offset="0%" stopColor="#0061FF" stopOpacity="0.03" />
                <stop offset="100%" stopColor="transparent" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Background glow */}
            <circle cx={CENTER} cy={CENTER} r={MAX_RADIUS + 10} fill="url(#wheelBg)" />

            {/* Concentric rings */}
            {Array.from({ length: RINGS }, (_, i) => {
              const r = INNER_RADIUS + ((MAX_RADIUS - INNER_RADIUS) * (i + 1)) / RINGS
              return (
                <circle
                  key={`ring-${i}`}
                  cx={CENTER} cy={CENTER} r={r}
                  fill="none"
                  stroke="rgba(255,255,255,0.04)"
                  strokeWidth={0.5}
                />
              )
            })}

            {/* Axis lines */}
            {SEGMENTS.map((seg, i) => {
              const angle = i * angleStep
              const outer = pointAt(angle, MAX_RADIUS)
              return (
                <line
                  key={`axis-${seg.id}`}
                  x1={CENTER} y1={CENTER}
                  x2={outer.x} y2={outer.y}
                  stroke="rgba(255,255,255,0.04)"
                  strokeWidth={0.5}
                />
              )
            })}

            {/* Filled polygon */}
            <motion.polygon
              points={polygonPoints}
              fill="rgba(0,97,255,0.12)"
              stroke="rgba(0,97,255,0.4)"
              strokeWidth={1.5}
              strokeLinejoin="round"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
            />

            {/* Score dots on vertices */}
            {SEGMENTS.map((seg, i) => {
              const score = (segmentMap[seg.id] ?? 5)
              const radius = INNER_RADIUS + ((MAX_RADIUS - INNER_RADIUS) * score) / 10
              const p = pointAt(i * angleStep, radius)
              return (
                <motion.circle
                  key={`dot-${seg.id}`}
                  cx={p.x} cy={p.y} r={4}
                  fill={seg.color}
                  stroke="#0e1621"
                  strokeWidth={2}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: i * 0.05, type: 'spring', stiffness: 400 }}
                />
              )
            })}

            {/* Labels + emojis (outside circle) */}
            {SEGMENTS.map((seg, i) => {
              const angle = i * angleStep
              const labelRadius = MAX_RADIUS + 26
              const p = pointAt(angle, labelRadius)
              const score = (segmentMap[seg.id] ?? 5)
              return (
                <g
                  key={`label-${seg.id}`}
                  className="cursor-pointer"
                  onClick={() => handleSegmentTap(seg.id)}
                >
                  {/* Tap target circle */}
                  <circle cx={p.x} cy={p.y} r={20} fill="transparent" />
                  <text
                    x={p.x} y={p.y - 6}
                    textAnchor="middle"
                    fontSize={14}
                    dominantBaseline="central"
                  >
                    {seg.emoji}
                  </text>
                  <text
                    x={p.x} y={p.y + 10}
                    textAnchor="middle"
                    fill={seg.color}
                    fontSize={8}
                    fontWeight={700}
                  >
                    {score}
                  </text>
                </g>
              )
            })}

            {/* Center score */}
            <circle cx={CENTER} cy={CENTER} r={INNER_RADIUS - 2} fill="rgba(0,97,255,0.08)" />
            <text
              x={CENTER} y={CENTER + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#F0F0F5"
              fontSize={14}
              fontWeight={800}
              fontFamily="Cabinet Grotesk, sans-serif"
            >
              {avg.toFixed(1)}
            </text>
          </svg>
        </div>

        {/* Segment labels row */}
        <div className="grid grid-cols-4 gap-1.5 px-3 pb-4">
          {SEGMENTS.map((seg) => {
            const score = (segmentMap[seg.id] ?? 5)
            return (
              <button
                key={seg.id}
                onClick={() => handleSegmentTap(seg.id)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/[0.02] border border-white/[0.04] tap-highlight-transparent"
              >
                <span className="text-[10px]">{seg.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[8px] text-hint truncate">{t(seg.labelKey)}</div>
                  <div className="h-1 mt-0.5 rounded-full bg-white/[0.04] overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: seg.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${score * 10}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Edit sheet */}
      {editingSegmentId && (
        <WheelSegmentSheet
          isOpen={true}
          segmentId={editingSegmentId}
          config={SEGMENTS.find((s) => s.id === editingSegmentId)!}
        />
      )}
    </>
  )
})
